import { Component, Input, Output, EventEmitter } from '@angular/core';

interface AggregatedRow {
    contentId: string;
    title: string;
    type: 'blog' | 'social-post' | 'newsletter';
    typeName: string;
    platform: string;
    views: number;
    likes: number;
    shares: number;
    comments: number;
    sparklinePoints: string;
    hasSparkline: boolean;
}

type SortField = 'title' | 'typeName' | 'platform' | 'views' | 'likes' | 'shares' | 'comments';
type SortDir = 'asc' | 'desc';
type DateRange = 7 | 30 | 90;

@Component({
    standalone: false,
    selector: 'mkt-analytics-section',
    template: `
        <!-- Date Range Filter -->
        <div class="ans-toolbar">
            <div class="ans-toolbar__range">
                <button class="ans-range-btn"
                        [class.ans-range-btn--active]="dateRange === 7"
                        (click)="setDateRange(7)">7d</button>
                <button class="ans-range-btn"
                        [class.ans-range-btn--active]="dateRange === 30"
                        (click)="setDateRange(30)">30d</button>
                <button class="ans-range-btn"
                        [class.ans-range-btn--active]="dateRange === 90"
                        (click)="setDateRange(90)">90d</button>
            </div>
            <button class="ans-pull-btn" (click)="pullMetrics.emit()" [disabled]="isPulling">
                <i class="fa-solid" [ngClass]="isPulling ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'"></i>
                {{ isPulling ? 'Pulling...' : 'Pull Metrics' }}
            </button>
        </div>

        <!-- Empty State -->
        <div *ngIf="rows.length === 0" class="ans-empty">
            <i class="fa-solid fa-chart-line ans-empty__icon"></i>
            <p class="ans-empty__text">No performance data yet.</p>
            <button class="ans-pull-btn ans-pull-btn--lg" (click)="pullMetrics.emit()" [disabled]="isPulling">
                <i class="fa-solid" [ngClass]="isPulling ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'"></i>
                {{ isPulling ? 'Pulling metrics...' : 'Pull Metrics Now' }}
            </button>
        </div>

        <!-- Table -->
        <div *ngIf="rows.length > 0" class="ans-table-wrap">
            <table class="ans-table">
                <thead>
                    <tr>
                        <th class="ans-th ans-th--text" (click)="toggleSort('title')">
                            Content Title
                            <i class="fa-solid" [ngClass]="sortIcon('title')"></i>
                        </th>
                        <th class="ans-th ans-th--text" (click)="toggleSort('typeName')">
                            Type
                            <i class="fa-solid" [ngClass]="sortIcon('typeName')"></i>
                        </th>
                        <th class="ans-th ans-th--text" (click)="toggleSort('platform')">
                            Platform
                            <i class="fa-solid" [ngClass]="sortIcon('platform')"></i>
                        </th>
                        <th class="ans-th ans-th--num" (click)="toggleSort('views')">
                            Views
                            <i class="fa-solid" [ngClass]="sortIcon('views')"></i>
                        </th>
                        <th class="ans-th ans-th--num" (click)="toggleSort('likes')">
                            Likes
                            <i class="fa-solid" [ngClass]="sortIcon('likes')"></i>
                        </th>
                        <th class="ans-th ans-th--num" (click)="toggleSort('shares')">
                            Shares
                            <i class="fa-solid" [ngClass]="sortIcon('shares')"></i>
                        </th>
                        <th class="ans-th ans-th--num" (click)="toggleSort('comments')">
                            Comments
                            <i class="fa-solid" [ngClass]="sortIcon('comments')"></i>
                        </th>
                        <th class="ans-th ans-th--sparkline">Trend</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let row of rows"
                        class="ans-row"
                        (click)="openContent.emit({ type: row.type, id: row.contentId })">
                        <td class="ans-td ans-td--title">{{ row.title }}</td>
                        <td class="ans-td ans-td--type">{{ row.typeName }}</td>
                        <td class="ans-td ans-td--platform">{{ row.platform }}</td>
                        <td class="ans-td ans-td--num">{{ row.views | number }}</td>
                        <td class="ans-td ans-td--num">{{ row.likes | number }}</td>
                        <td class="ans-td ans-td--num">{{ row.shares | number }}</td>
                        <td class="ans-td ans-td--num">{{ row.comments | number }}</td>
                        <td class="ans-td ans-td--sparkline">
                            <svg *ngIf="row.hasSparkline"
                                 class="ans-sparkline"
                                 viewBox="0 0 80 24"
                                 preserveAspectRatio="none">
                                <polyline [attr.points]="row.sparklinePoints"
                                          fill="none"
                                          stroke="var(--mj-brand-primary, #6366f1)"
                                          stroke-width="1.5"
                                          stroke-linejoin="round"
                                          stroke-linecap="round" />
                            </svg>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    `,
    styles: [`
        :host {
            display: block;
        }

        /* ─── Toolbar ─────────────────────────────── */
        .ans-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 16px;
        }
        .ans-pull-btn {
            display: inline-flex; align-items: center; gap: 6px;
            padding: 6px 14px; font-size: 12px; font-weight: 600; border-radius: 6px;
            border: 1px solid var(--mj-brand-primary, #6366f1); background: var(--mj-brand-primary, #6366f1);
            color: #fff; cursor: pointer; transition: all 0.15s;
        }
        .ans-pull-btn:hover:not(:disabled) { filter: brightness(1.1); }
        .ans-pull-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ans-pull-btn--lg { padding: 10px 20px; font-size: 14px; margin-top: 16px; }
        .ans-toolbar__range {
            display: flex;
            gap: 4px;
            background: var(--mj-bg-surface, #f8f9fa);
            border: 1px solid var(--mj-border-default, #e2e8f0);
            border-radius: 6px;
            padding: 2px;
        }
        .ans-range-btn {
            padding: 4px 12px;
            border: none;
            border-radius: 4px;
            background: transparent;
            color: var(--mj-text-secondary, #64748b);
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.15s ease;
        }
        .ans-range-btn:hover {
            color: var(--mj-text-primary, #1e293b);
        }
        .ans-range-btn--active {
            background: #fff;
            color: var(--mj-text-primary, #1e293b);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        }

        /* ─── Empty State ─────────────────────────── */
        .ans-empty {
            text-align: center;
            padding: 64px 24px;
            color: var(--mj-text-secondary, #64748b);
        }
        .ans-empty__icon {
            font-size: 32px;
            margin-bottom: 12px;
            opacity: 0.4;
        }
        .ans-empty__text {
            font-size: 14px;
            line-height: 1.5;
            max-width: 400px;
            margin: 0 auto;
        }

        /* ─── Table ───────────────────────────────── */
        .ans-table-wrap {
            overflow-x: auto;
        }
        .ans-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
        }

        /* Header */
        .ans-th {
            padding: 8px 12px;
            text-align: left;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            color: var(--mj-text-secondary, #64748b);
            border-bottom: 2px solid var(--mj-border-default, #e2e8f0);
            cursor: pointer;
            user-select: none;
            white-space: nowrap;
        }
        .ans-th:hover {
            color: var(--mj-text-primary, #1e293b);
        }
        .ans-th i {
            margin-left: 4px;
            font-size: 10px;
            opacity: 0.5;
        }
        .ans-th--num {
            text-align: right;
        }
        .ans-th--sparkline {
            text-align: center;
            cursor: default;
        }

        /* Rows */
        .ans-row {
            cursor: pointer;
            transition: background 0.1s ease;
        }
        .ans-row:hover {
            background: var(--mj-bg-surface, #f8f9fa);
        }

        /* Cells */
        .ans-td {
            padding: 10px 12px;
            border-bottom: 1px solid var(--mj-border-default, #e2e8f0);
            color: var(--mj-text-primary, #1e293b);
            vertical-align: middle;
        }
        .ans-td--title {
            font-weight: 500;
            max-width: 280px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .ans-td--type,
        .ans-td--platform {
            color: var(--mj-text-secondary, #64748b);
        }
        .ans-td--num {
            text-align: right;
            font-variant-numeric: tabular-nums;
        }
        .ans-td--sparkline {
            text-align: center;
            width: 96px;
        }

        /* ─── Sparkline ──────────────────────────── */
        .ans-sparkline {
            width: 80px;
            height: 24px;
            display: inline-block;
            vertical-align: middle;
        }
    `]
})
export class AnalyticsSectionComponent {
    @Input() metrics: {
        ID: string; ContentTypeID: string; ContentID: string; Platform: string;
        MetricDate: string; PageViews: number | null; Likes: number | null;
        SocialShares: number | null; Comments: number | null; UniqueVisitors: number | null;
    }[] = [];
    @Input() blogs: any[] = [];
    @Input() socialPosts: any[] = [];
    @Input() newsletters: any[] = [];
    @Input() contentTypes: any[] = [];

    @Output() openContent = new EventEmitter<{ type: 'blog' | 'social-post' | 'newsletter'; id: string }>();
    @Output() pullMetrics = new EventEmitter<void>();
    @Input() isPulling = false;

    dateRange: DateRange = 30;
    sortField: SortField = 'views';
    sortDir: SortDir = 'desc';

    /** Cached aggregated + sorted rows, rebuilt on any input change */
    private _rows: AggregatedRow[] = [];
    private _dirty = true;

    get rows(): AggregatedRow[] {
        if (this._dirty) {
            this._rows = this.buildRows();
            this._dirty = false;
        }
        return this._rows;
    }

    setDateRange(range: DateRange): void {
        if (this.dateRange === range) return;
        this.dateRange = range;
        this._dirty = true;
    }

    toggleSort(field: SortField): void {
        if (this.sortField === field) {
            this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDir = field === 'title' || field === 'typeName' || field === 'platform' ? 'asc' : 'desc';
        }
        this._dirty = true;
    }

    sortIcon(field: SortField): string {
        if (this.sortField !== field) return 'fa-sort';
        return this.sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    }

    // ─── Pure aggregation ────────────────────────────────

    private buildRows(): AggregatedRow[] {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - this.dateRange);
        const cutoffTime = cutoff.getTime();

        // Filter metrics within date range
        const filtered = this.metrics.filter(m => new Date(m.MetricDate).getTime() >= cutoffTime);

        // Group by ContentID
        const groups = new Map<string, typeof filtered>();
        for (const m of filtered) {
            const existing = groups.get(m.ContentID);
            if (existing) {
                existing.push(m);
            } else {
                groups.set(m.ContentID, [m]);
            }
        }

        // Build rows
        const rows: AggregatedRow[] = [];
        for (const [contentId, group] of groups) {
            const resolved = this.resolveContent(contentId);
            if (!resolved) continue;

            const views = sum(group, m => m.PageViews);
            const likes = sum(group, m => m.Likes);
            const shares = sum(group, m => m.SocialShares);
            const comments = sum(group, m => m.Comments);
            const platform = group[0].Platform || '—';

            // Sparkline: daily PageViews sorted by date
            const sorted = [...group].sort((a, b) =>
                new Date(a.MetricDate).getTime() - new Date(b.MetricDate).getTime()
            );
            const dailyViews = sorted.map(m => m.PageViews ?? 0);
            const sparklinePoints = buildSparklinePoints(dailyViews);

            rows.push({
                contentId,
                title: resolved.title,
                type: resolved.type,
                typeName: resolved.typeName,
                platform,
                views,
                likes,
                shares,
                comments,
                sparklinePoints,
                hasSparkline: dailyViews.length > 1,
            });
        }

        // Sort
        return sortRows(rows, this.sortField, this.sortDir);
    }

    private resolveContent(contentId: string): { title: string; type: 'blog' | 'social-post' | 'newsletter'; typeName: string } | null {
        const blog = this.blogs.find(b => b.ID === contentId);
        if (blog) {
            return { title: blog.Title || 'Untitled Blog', type: 'blog', typeName: 'Blog' };
        }

        const post = this.socialPosts.find(sp => sp.ID === contentId);
        if (post) {
            const body: string = post.Body || '';
            const title = body.length > 60 ? body.slice(0, 60) + '…' : body || 'Untitled Post';
            return { title, type: 'social-post', typeName: 'Social' };
        }

        const newsletter = this.newsletters.find(n => n.ID === contentId);
        if (newsletter) {
            return { title: newsletter.Subject || 'Untitled Newsletter', type: 'newsletter', typeName: 'Newsletter' };
        }

        return null;
    }
}

// ─── Pure helpers (no side effects) ──────────────────────

function sum(items: readonly { [k: string]: any }[], accessor: (item: any) => number | null): number {
    let total = 0;
    for (const item of items) {
        total += accessor(item) ?? 0;
    }
    return total;
}

function buildSparklinePoints(values: readonly number[]): string {
    if (values.length < 2) return '';
    const maxVal = Math.max(...values, 1); // avoid division by zero
    const xStep = 80 / (values.length - 1);
    const padding = 2; // vertical padding so line doesn't clip
    const yRange = 24 - padding * 2;

    return values
        .map((v, i) => {
            const x = (i * xStep).toFixed(1);
            const y = (padding + yRange - (v / maxVal) * yRange).toFixed(1);
            return `${x},${y}`;
        })
        .join(' ');
}

function sortRows(rows: AggregatedRow[], field: SortField, dir: SortDir): AggregatedRow[] {
    const mult = dir === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];
        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return mult * aVal.localeCompare(bVal);
        }
        return mult * ((aVal as number) - (bVal as number));
    });
}
